import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Inbox,
  CalendarDays,
  Phone,
  Mail,
  Users,
  Trash2,
  ExternalLink,
  Images,
  UploadCloud,
  Loader2,
  GripVertical,
  Star,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/context/AuthContext";
import { api, toISODate, parseISO } from "@/lib/api";
import { ALBUMS } from "@/lib/data";
import { Logo } from "@/components/Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["new", "contacted", "booked", "archived"];
const statusColor = {
  new: "bg-[var(--gold)]/15 text-[var(--gold-hover)]",
  contacted: "bg-blue-100 text-blue-700",
  booked: "bg-green-100 text-green-700",
  archived: "bg-stone-200 text-stone-500",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [booked, setBooked] = useState([]); // array of "YYYY-MM-DD"
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const loadInquiries = useCallback(async () => {
    try {
      const { data } = await api.get("/inquiries");
      setInquiries(data);
    } catch {
      toast.error("Failed to load inquiries.");
    }
  }, []);

  const loadAvailability = useCallback(async () => {
    const { data } = await api.get("/availability");
    setBooked(data.map((d) => d.date));
  }, []);

  const loadGallery = useCallback(async () => {
    const { data } = await api.get("/gallery");
    setGallery(data);
  }, []);

  useEffect(() => {
    loadInquiries();
    loadAvailability();
    loadGallery();
  }, [loadInquiries, loadAvailability, loadGallery]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post("/gallery/upload", fd);
        ok += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded.`);
    e.target.value = "";
    setUploading(false);
    loadGallery();
  };

  const removeImage = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setGallery((prev) => prev.filter((g) => g.id !== id));
      toast.success("Photo removed.");
    } catch {
      toast.error("Could not remove photo.");
    }
  };

  const setCaption = (id, caption) =>
    setGallery((prev) => prev.map((g) => (g.id === id ? { ...g, caption } : g)));

  const saveCaption = async (id, caption) => {
    try {
      await api.patch(`/gallery/${id}`, { caption });
    } catch {
      toast.error("Caption not saved.");
    }
  };

  const setField = (id, patch) =>
    setGallery((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));

  const saveField = async (id, patch) => {
    try {
      await api.patch(`/gallery/${id}`, patch);
    } catch {
      toast.error("Update failed.");
      loadGallery();
    }
  };

  const toggleFeatured = (g) => {
    const v = !g.featured;
    setField(g.id, { featured: v });
    saveField(g.id, { featured: v });
  };

  const changeAlbum = (id, album) => {
    setField(id, { album });
    saveField(id, { album });
  };

  const handleDrop = async (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...gallery];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setGallery(next);
    setDragIndex(null);
    try {
      await api.put("/gallery/reorder", { ids: next.map((g) => g.id) });
      toast.success("Order updated.");
    } catch {
      toast.error("Could not save order.");
      loadGallery();
    }
  };

  const setStatus = async (id, status) => {
    setInquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Update failed.");
      loadInquiries();
    }
  };

  const removeInquiry = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries((prev) => prev.filter((q) => q.id !== id));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const toggleDate = async (day) => {
    const iso = toISODate(day);
    const isBooked = booked.includes(iso);
    try {
      await api.post("/availability", { date: iso, status: isBooked ? "available" : "booked" });
      setBooked((prev) => (isBooked ? prev.filter((d) => d !== iso) : [...prev, iso]));
    } catch {
      toast.error("Could not update availability.");
    }
  };

  const bookedDates = booked.map(parseISO);
  const newCount = inquiries.filter((q) => q.status === "new").length;

  return (
    <div className="min-h-screen bg-[var(--surface-subtle)]">
      <header className="sticky top-0 z-30 bg-[var(--bg)]/90 backdrop-blur-xl border-b border-[var(--border-gold)]">
        <div className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-9 w-auto" />
            <span className="hidden sm:block font-accent text-xs tracking-[0.25em] text-[var(--ink-3)] border-l border-[var(--border-gold)] pl-4">
              STUDIO
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="flex items-center gap-1.5 text-sm text-[var(--ink-2)] hover:text-[var(--ink)]"
              data-testid="admin-view-site"
            >
              <ExternalLink size={15} /> View site
            </a>
            <button
              onClick={() => { logout(); navigate("/admin"); }}
              className="flex items-center gap-1.5 rounded-full border border-[var(--border-gold)] px-4 py-2 text-sm hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <h1 className="font-serif text-4xl tracking-tight mb-1">
          Good day, {user?.name || "Admin"}.
        </h1>
        <p className="text-[var(--ink-2)] mb-8">
          {newCount} new {newCount === 1 ? "inquiry" : "inquiries"} · {booked.length} booked dates
        </p>

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-[var(--bg)] border border-[var(--border-gold)]">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">
              <Inbox size={15} className="mr-2" /> Inquiries ({inquiries.length})
            </TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">
              <CalendarDays size={15} className="mr-2" /> Availability
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">
              <Images size={15} className="mr-2" /> Gallery ({gallery.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries" className="mt-6">
            {inquiries.length === 0 ? (
              <div className="text-center py-20 text-[var(--ink-3)]" data-testid="inquiries-empty">
                No inquiries yet.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="inquiries-list">
                {inquiries.map((q) => (
                  <div
                    key={q.id}
                    className="bg-[var(--bg)] border border-[var(--border-gold)] rounded-[3px] p-6"
                    data-testid={`inquiry-card-${q.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-serif text-2xl">{q.name}</h3>
                        <span
                          className={`inline-block mt-1 text-[11px] uppercase tracking-wide px-2.5 py-0.5 rounded-full ${statusColor[q.status] || ""}`}
                        >
                          {q.status}
                        </span>
                      </div>
                      <button
                        onClick={() => removeInquiry(q.id)}
                        className="text-[var(--ink-3)] hover:text-red-500 transition-colors"
                        data-testid={`inquiry-delete-${q.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="mt-4 space-y-1.5 text-sm text-[var(--ink-2)]">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-[var(--gold)]">
                        <Mail size={14} /> {q.email}
                      </a>
                      <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-[var(--gold)]">
                        <Phone size={14} /> {q.phone}
                      </a>
                      {q.event_date && (
                        <div className="flex items-center gap-2">
                          <CalendarDays size={14} /> {q.event_date}
                        </div>
                      )}
                      {q.guest_count && (
                        <div className="flex items-center gap-2">
                          <Users size={14} /> {q.guest_count} guests
                        </div>
                      )}
                    </div>

                    {q.service && (
                      <div className="mt-3 text-sm">
                        <span className="text-[var(--ink-3)]">Interest: </span>
                        <span className="text-[var(--ink)]">{q.service}</span>
                      </div>
                    )}
                    {q.message && (
                      <p className="mt-3 text-sm text-[var(--ink-2)] italic border-l-2 border-[var(--border-gold)] pl-3">
                        “{q.message}”
                      </p>
                    )}

                    <div className="mt-5 flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatus(q.id, s)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            q.status === s
                              ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                              : "border-[var(--border-gold)] text-[var(--ink-2)] hover:border-[var(--gold)]"
                          }`}
                          data-testid={`inquiry-status-${s}-${q.id}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-[var(--bg)] border border-[var(--border-gold)] rounded-[3px] p-6">
                <h3 className="font-serif text-2xl mb-1">Manage dates</h3>
                <p className="text-sm text-[var(--ink-2)] mb-4">
                  Click a date to mark it booked. Click again to free it. Couples see this live.
                </p>
                <Calendar
                  mode="default"
                  onDayClick={toggleDate}
                  modifiers={{ booked: bookedDates }}
                  modifiersClassNames={{
                    booked: "bg-red-500 text-white rounded-md hover:bg-red-600",
                  }}
                  data-testid="availability-calendar"
                />
                <div className="flex gap-5 mt-4 text-xs text-[var(--ink-2)]">
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-red-500" /> Booked</span>
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded border border-[var(--border-gold)]" /> Available</span>
                </div>
              </div>

              <div className="bg-[var(--bg)] border border-[var(--border-gold)] rounded-[3px] p-6">
                <h3 className="font-serif text-2xl mb-4">Booked dates ({booked.length})</h3>
                {booked.length === 0 ? (
                  <p className="text-sm text-[var(--ink-3)]">No dates blocked yet.</p>
                ) : (
                  <ul className="space-y-2" data-testid="booked-list">
                    {[...booked].sort().map((d) => (
                      <li
                        key={d}
                        className="flex items-center justify-between border-b border-[var(--border-ink)] pb-2 text-sm"
                      >
                        <span>{parseISO(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
                        <button
                          onClick={() => toggleDate(parseISO(d))}
                          className="text-[var(--ink-3)] hover:text-[var(--gold)]"
                          data-testid={`free-date-${d}`}
                        >
                          Free up
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="gallery" className="mt-6">
            <div className="bg-[var(--bg)] border border-[var(--border-gold)] rounded-[3px] p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl mb-1">Wedding photos</h3>
                  <p className="text-sm text-[var(--ink-2)]">
                    Upload JPG, PNG or WEBP (max 10MB each). These replace the gallery on your site.
                  </p>
                </div>
                <label
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] text-[var(--bg)] px-6 py-3 text-sm tracking-wide hover:bg-[var(--gold)] transition-colors cursor-pointer whitespace-nowrap"
                  data-testid="gallery-upload-label"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                  {uploading ? "Uploading…" : "Upload photos"}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    disabled={uploading}
                    onChange={handleUpload}
                    data-testid="gallery-upload-input"
                  />
                </label>
              </div>
            </div>

            {gallery.length === 0 ? (
              <div className="text-center py-20 text-[var(--ink-3)]" data-testid="gallery-empty">
                No photos uploaded yet — your site shows sample images until you add your own.
              </div>
            ) : (
              <>
                <p className="text-xs text-[var(--ink-3)] mb-3">
                  Drag photos by the handle to reorder how they appear on your site. Add a caption that
                  shows on hover.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-gallery-grid">
                  {gallery.map((g, i) => (
                    <div
                      key={g.id}
                      draggable
                      onDragStart={() => setDragIndex(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(i)}
                      className={`group relative rounded-[3px] border-2 transition-opacity ${
                        dragIndex === i
                          ? "opacity-40 border-[var(--gold)]"
                          : g.featured
                          ? "border-[var(--gold)]"
                          : "border-[var(--border-gold)]"
                      }`}
                      data-testid={`admin-gallery-item-${g.id}`}
                    >
                      <div className="relative aspect-square overflow-hidden rounded-t-[2px]">
                        <img src={`${BACKEND}${g.url}`} alt="Gallery" className="w-full h-full object-cover" />
                        <span
                          className="absolute top-2 left-2 h-9 w-9 rounded-full bg-[var(--bg)]/90 text-[var(--ink)] flex items-center justify-center cursor-grab active:cursor-grabbing shadow-sm"
                          title="Drag to reorder"
                          data-testid={`gallery-drag-${g.id}`}
                        >
                          <GripVertical size={16} />
                        </span>
                        <div className="absolute top-2 right-2 flex gap-1.5">
                          <button
                            onClick={() => toggleFeatured(g)}
                            className={`h-9 w-9 rounded-full flex items-center justify-center shadow-sm transition-colors ${
                              g.featured
                                ? "bg-[var(--gold)] text-white"
                                : "bg-[var(--bg)]/90 text-[var(--ink-3)] hover:text-[var(--gold)] opacity-0 group-hover:opacity-100"
                            }`}
                            title={g.featured ? "Featured — click to unfeature" : "Feature this photo"}
                            data-testid={`gallery-feature-${g.id}`}
                          >
                            <Star size={15} fill={g.featured ? "currentColor" : "none"} />
                          </button>
                          <button
                            onClick={() => removeImage(g.id)}
                            className="h-9 w-9 rounded-full bg-[var(--ink)]/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                            data-testid={`gallery-delete-${g.id}`}
                            aria-label="Delete photo"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <span className="absolute bottom-2 left-2 text-[10px] font-accent tracking-wider bg-[var(--ink)]/70 text-white px-2 py-0.5 rounded-full">
                          #{i + 1}
                        </span>
                      </div>
                      <select
                        value={g.album || "Wedding"}
                        onChange={(e) => changeAlbum(g.id, e.target.value)}
                        className="w-full bg-[var(--surface-subtle)] text-xs px-3 py-2 outline-none border-b border-[var(--border-ink)] text-[var(--ink)] cursor-pointer"
                        data-testid={`gallery-album-${g.id}`}
                      >
                        {ALBUMS.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                      <input
                        value={g.caption || ""}
                        onChange={(e) => setCaption(g.id, e.target.value)}
                        onBlur={(e) => saveCaption(g.id, e.target.value)}
                        placeholder="Add a caption…"
                        className="w-full bg-[var(--bg)] text-sm px-3 py-2.5 rounded-b-[2px] outline-none placeholder:text-[var(--ink-3)] focus:bg-[var(--surface-subtle)]"
                        data-testid={`gallery-caption-${g.id}`}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
